class SorellaFinder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.loadCatalog(this);
    this.steps = [...this.querySelectorAll('[data-finder-step]')];
    this.form = this.querySelector('[data-finder-form]');
    this.results = this.querySelector('[data-finder-results]');
    this.index = 0;
    this.querySelector('[data-finder-next]')?.addEventListener('click', () => this.next());
    this.querySelector('[data-finder-back]')?.addEventListener('click', () => this.back());
    this.querySelector('[data-finder-restart]')?.addEventListener('click', () => this.restart());
    this.form?.addEventListener('change', () => this.updateNextState());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-finder-retry]')) this.retryCatalog();
    });
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-card-variant]');
      if (input?.checked) this.updateCardImage(input);
    });
    this.querySelector('[data-multi-choice]')?.addEventListener('change', (event) => {
      const selected = [...this.querySelectorAll('input[name="accords"]:checked')];
      if (selected.length > 3) {
        event.target.checked = false;
        const hint = this.querySelector('[data-finder-multi-hint]');
        if (hint) hint.textContent = 'Three is perfect. Unpick one if you want to swap.';
      }
    });
    this.renderStep();
  }

  loadCatalog(source) {
    this.products = [];
    this.catalogState = 'load-error';
    if (!source || source.dataset.finderCatalogState === 'missing-collection') return false;
    try {
      const script = source.querySelector('[data-finder-products]');
      if (!script) return false;
      const products = JSON.parse(script.textContent);
      const isId = (id) => /^\d+$/.test(String(id || ''));
      if (!Array.isArray(products) || !products.every((product) => product
        && isId(product.id) && typeof product.title === 'string' && typeof product.url === 'string'
        && typeof product.available === 'boolean' && Array.isArray(product.variants)
        && product.variants.every((variant) => variant && isId(variant.id) && typeof variant.available === 'boolean'))) return false;
      this.products = products;
      this.catalogState = products.length ? 'ready' : 'empty-catalog';
      return true;
    } catch (_) {
      // A broken data feed is not evidence that perfumes are sold out.
      return false;
    }
  }

  async retryCatalog() {
    if (this.catalogRetryPending) return;
    this.catalogRetryPending = true;
    const controller = new AbortController();
    this.catalogRetryController = controller;
    const timer = setTimeout(() => controller.abort(), 10000);
    const button = this.querySelector('[data-finder-retry]');
    if (button) { button.disabled = true; button.textContent = 'Loading scents…'; }
    this.results.setAttribute('aria-busy', 'true');
    try {
      if (!this.dataset.finderSectionId) throw new Error('Finder section is missing');
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.finderSectionId);
      const response = await fetch(url.href, { headers: { Accept: 'text/html' }, cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Finder could not refresh');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      // Restart/disconnect invalidates this request, even if fetch resolves late.
      if (this.catalogRetryController !== controller) return;
      this.loadCatalog(document.querySelector('sorella-finder'));
    } catch (_) {
      if (this.catalogRetryController !== controller) return;
      this.products = [];
      this.catalogState = 'load-error';
    } finally {
      clearTimeout(timer);
      if (this.catalogRetryController === controller) {
        this.catalogRetryController = null;
        this.catalogRetryPending = false;
        this.results.removeAttribute('aria-busy');
        this.showResults();
      }
    }
  }

  cancelCatalogRetry() {
    const controller = this.catalogRetryController;
    this.catalogRetryController = null;
    this.catalogRetryPending = false;
    controller?.abort();
    this.results?.removeAttribute('aria-busy');
  }

  disconnectedCallback() { this.cancelCatalogRetry(); }

  next() {
    if (this.index < this.steps.length - 1) { this.index += 1; this.renderStep(); return; }
    this.showResults();
  }

  back() {
    if (this.index === 0) return;
    this.index -= 1;
    this.renderStep();
  }

  renderStep() {
    this.steps.forEach((step, index) => step.hidden = index !== this.index);
    this.querySelector('[data-finder-back]').disabled = this.index === 0;
    const next = this.querySelector('[data-finder-next]');
    next.innerHTML = this.index === this.steps.length - 1 ? 'See my edit <span aria-hidden="true">→</span>' : 'Next <span aria-hidden="true">→</span>';
    const percentage = ((this.index + 1) / this.steps.length) * 100;
    this.querySelector('[data-finder-progress]').style.width = `${percentage}%`;
    this.querySelector('[data-finder-progress-label]').textContent = `${String(this.index + 1).padStart(2,'0')} / ${String(this.steps.length).padStart(2,'0')}`;
    this.steps[this.index]?.querySelector('legend')?.focus({preventScroll:true});
    this.updateNextState();
  }

  updateNextState() {
    const step = this.steps[this.index];
    const next = this.querySelector('[data-finder-next]');
    if (!step || !next) return;
    const hasAnswer = Boolean(step.querySelector('input:checked'));
    next.disabled = !hasAnswer;
  }

  answers() {
    const data = new FormData(this.form);
    return {
      gender:data.get('gender') || 'Any', personality:data.get('personality') || 'Any', occasion:data.get('occasion') || 'Any',
      family:data.get('family') || 'Any', accords:data.getAll('accords'), season:data.get('season') || 'All Year', origin:data.get('origin') || 'Any',
      weather:this.weatherAnswer()
    };
  }

  weatherAnswer() {
    try {
      const weather = JSON.parse(sessionStorage.getItem('sorella-weather-v2') || 'null');
      const temperature = Number(weather?.temperature);
      if (!Number.isFinite(temperature) || Date.now() - Number(weather?.savedAt || 0) > 30 * 60 * 1000) return '';
      const code = Number(weather?.weatherCode);
      if ((code >= 51 && code <= 82) || code >= 95) return 'rainy';
      if (temperature < 10) return 'cold';
      if (temperature < 20) return 'cool';
      if (temperature < 30) return 'pleasant';
      const isNight = Number(weather?.isDay) === 0 || (weather?.isDay == null && (new Date().getHours() >= 18 || new Date().getHours() < 6));
      return isNight ? 'summer_night' : 'hot_day';
    } catch (_) { return ''; }
  }

  ownerScore(product, answers) {
    const owner = product.profile?.ownerScoring;
    if (!owner?.scores || owner.include === false) return null;
    const selections = [
      ['gender', answers.gender],
      ['personality', answers.personality],
      ['occasion', answers.occasion],
      ['family', answers.family],
      ...(answers.accords || []).map((answer) => ['accords', answer]),
      ...(answers.weather ? [['weather', answers.weather]] : []),
    ];
    let score = 0;
    for (const [group, answer] of selections) score += Number(owner.scores?.[group]?.[answer] || 0);
    return {
      score,
      priority:Number(owner.priority || 0),
      reasons:owner.reason ? [String(owner.reason)] : [],
    };
  }

  score(product, answers) {
    const ownerResult = this.ownerScore(product, answers);
    if (ownerResult) return ownerResult;
    const p = product.profile || {};
    let score = p.bestseller ? .25 : 0;
    const reasons = [];
    if (answers.gender !== 'Any' && (p.gender === answers.gender || p.gender === 'Unisex')) { score += 3; reasons.push(`reads ${answers.gender.toLowerCase()}`); }
    const personalityMap = {Quiet:['Powdery','Floral','Musk'],Warm:['Warm','Vanilla','Amber','Gourmand'],Bold:['Oud','Spicy','Woody'],Playful:['Fruity','Sweet','Floral'],Relaxed:['Fresh','Citrus','Aquatic','Herbal']};
    const productFamilies = [p.family,...(p.families || [])].filter(Boolean).map(value => String(value).toLowerCase());
    const productAccords = (p.accords || []).map(value => String(value).toLowerCase());
    const hasFamily = (value) => productFamilies.includes(String(value).toLowerCase());
    const hasAccord = (value) => productAccords.includes(String(value).toLowerCase());
    const personalityMatches = (personalityMap[answers.personality] || []).filter(value => hasAccord(value) || hasFamily(value));
    if (answers.personality !== 'Any' && personalityMatches.length) { score += Math.min(4, personalityMatches.length * 2); reasons.push(`${answers.personality.toLowerCase()} energy`); }
    const familyMap = {
      Floral:{families:['Floral'],accords:['Floral']},
      Gourmand:{families:['Gourmand'],accords:['Gourmand','Sweet']},
      Vanilla:{families:['Warm','Gourmand'],accords:['Vanilla']},
      Fruity:{families:['Fruity'],accords:['Fruity']},
      Fresh:{families:['Fresh','Herbal'],accords:['Fresh','Clean','Citrus','Aquatic']},
      Woody:{families:['Earthy'],accords:['Woody']},
      Musk:{families:['Warm','Earthy'],accords:['Musk','Powdery']},
      Oud:{families:['Earthy','Warm'],accords:['Oud','Woody']}
    };
    const familyPreference = familyMap[answers.family];
    if (familyPreference && (familyPreference.families.some(hasFamily) || familyPreference.accords.some(hasAccord))) { score += 4; reasons.push(`${answers.family.toLowerCase()} at heart`); }
    if (answers.occasion !== 'Any' && (p.occasions || []).includes(answers.occasion)) { score += 3; reasons.push(`made for ${answers.occasion.toLowerCase()}`); }
    if (answers.season !== 'All Year' && ((p.seasons || []).includes(answers.season) || (p.seasons || []).includes('All Year'))) { score += 2; reasons.push(`works in ${answers.season.toLowerCase()}`); }
    const accordMatches = answers.accords.filter(hasAccord);
    score += Math.min(4, accordMatches.length * 2);
    if (accordMatches.length) reasons.push(`${accordMatches.slice(0,2).join(' + ').toLowerCase()} notes`);
    if (answers.origin === 'Original' && p.original) { score += 2; reasons.push('a Sorella original'); }
    if (answers.origin === 'Inspired' && !p.original) { score += 2; reasons.push('a familiar signature, reworked'); }
    return {score,reasons,priority:0};
  }

  chooseVariant(product, format) {
    const available = (product.variants || []).filter(variant => variant.available);
    if (format !== 'Any') {
      const requested = available.find(variant => variant.format === format || variant.title === format);
      if (requested) return requested;
    }
    return available.find(variant => /(^|\s)30\s*ml($|\s)/i.test(`${variant.title} ${variant.format || ''}`))
      || available.find(variant => !/\b5\s*ml\b|tester/i.test(`${variant.title} ${variant.format || ''}`))
      || available[0];
  }

  productUrl(product) {
    const separator = String(product.url || '').includes('?') ? '&' : '?';
    return `${product.url}${separator}variant=${encodeURIComponent(product.variant.id)}`;
  }

  showResults() {
    const answers = this.answers();
    const matches = (this.catalogState === 'ready' ? this.products : []).filter(product => product.available && product.profile?.ownerScoring?.include !== false).map(product => {
      const variant = this.chooseVariant(product, 'Any');
      return variant ? {...product,variant,...this.score(product,answers)} : null;
    }).filter(Boolean)
      .sort((a,b) => b.score - a.score || Number(b.priority || 0) - Number(a.priority || 0) || a.title.localeCompare(b.title)).slice(0,3);
    this.form.hidden = true;
    this.results.hidden = false;
    this.classList.add('is-results');
    this.querySelector('.sfinder__stage')?.classList.add('is-results');
    this.querySelector('.sfinder__shell')?.classList.add('is-results');
    this.querySelector('.sfinder__progress').hidden = true;
    const grid = this.querySelector('[data-finder-result-grid]');
    this.results.classList.toggle('is-empty', matches.length === 0);
    const heading = this.results.querySelector('h2');
    if (heading) heading.textContent = matches.length === 3 ? 'Three scents that sound like you.' : matches.length ? 'Scents that sound like you.' : 'Let’s try that again.';
    grid.innerHTML = matches.length ? matches.map((product) => this.card(product)).join('') : this.catalogErrorMarkup();
    this.results.querySelector('h2')?.setAttribute('tabindex', '-1');
    this.results.querySelector('h2')?.focus?.({ preventScroll:true });
    // The final quiz button can leave mobile users below the new results.
    // Use the existing header scroll margin and avoid animation on recovery.
    this.results.scrollIntoView({ behavior:'instant', block:'start' });
  }

  catalogErrorMarkup() {
    const message = this.catalogState === 'load-error'
      ? 'We couldn’t load the scents. Your answers are saved. Please try again.'
      : this.catalogState === 'empty-catalog'
        ? 'The Finder has no scents to show right now. Your answers are saved. Try again or browse our perfumes.'
        : 'No Finder picks are available right now. Your answers are saved. Try again or browse our perfumes.';
    const shopUrl = this.dataset.finderShopUrl || `${window.Shopify?.routes?.root || '/'}collections/all-perfumes`;
    return `<div class="sfinder__empty" data-finder-error role="status"><p>${this.escape(message)}</p><div><button type="button" data-finder-retry>Try again</button><a href="${this.escape(shopUrl)}">Browse perfumes</a></div></div>`;
  }

  card(product) {
    const image = product.variant.image || product.image || '';
    const media = this.imageMarkup(image, product.title);
    const url = this.productUrl(product);
    const family = this.familyLabel(product.family || product.profile?.family || product.profile?.families?.[0]);
    const gender = product.gender || product.profile?.gender || '';
    const reviews = Number(product.reviewCount || 0);
    const variantChoices = (product.variants || []).filter((variant) => variant.available).map((variant) => {
      const variantImage = variant.image || product.image || '';
      return `<label><input type="radio" name="FinderCardVariant-${this.escape(product.id)}" value="${this.escape(variant.id)}" data-card-variant data-price="${this.escape(variant.price)}" data-variant-url="${this.escape(product.url)}?variant=${this.escape(variant.id)}" data-image-url="${this.escape(variantImage)}"${String(variant.id) === String(product.variant.id) ? ' checked' : ''}><span>${this.escape(this.variantLabel(variant.title, variant.format))}</span></label>`;
    }).join('');
    const origin = product.house
      ? `Inspired by<br><strong>${this.escape(product.house)}${product.inspiredFragrance ? `’s ${this.escape(product.inspiredFragrance)}` : ''}</strong>`
      : product.original
        ? 'Sorella<br><strong>Original composition</strong>'
        : `<strong>${this.escape(product.character || 'A Sorella scent')}</strong>`;
    return `<article class="sfinder-card dcatalog-card" data-product-card><a class="dcatalog-card__media" href="${this.escape(url)}">${media}</a><div class="dcatalog-card__body"><div class="dcatalog-card__heading"><h3><a href="${this.escape(url)}">${this.escape(product.title)}</a></h3><span class="dcatalog-card__tags">${family ? `<span class="dcatalog-card__family is-${this.escape(family.toLowerCase())}"><span>${this.escape(family)}</span><button type="button" class="dcatalog-card__family-info" data-open-family aria-haspopup="dialog" aria-label="Learn about Sorella scent families"><i aria-hidden="true">i</i></button></span>` : ''}${gender ? `<span class="dcatalog-card__gender">${this.escape(gender)}</span>` : ''}</span></div><p class="dcatalog-card__reviews"><span class="sorella-rating-stars" role="img" aria-label="5 out of 5 stars">${this.starMarkup()}</span>${reviews ? `<small>${this.escape(reviews)} reviews</small>` : ''}</p><p class="dcatalog-card__inspired">${origin}</p>${variantChoices ? `<div class="dcatalog-card__variants" aria-label="Choose ${this.escape(product.title)} size">${variantChoices}</div>` : ''}<div class="dcatalog-card__prices"><span>Price: <strong data-card-price>${this.escape(product.variant.price)}</strong></span></div><button class="dcatalog-card__add" type="button" data-add-to-cart data-variant-id="${this.escape(product.variant.id)}"><span>Add to cart</span></button><p class="dcatalog-card__error" data-add-error role="status"></p></div></article>`;
  }

  imageMarkup(image, title) {
    return image ? `<img src="${this.escape(image)}" alt="${this.escape(title)}" loading="lazy">`
      : '<span class="sfinder-card__no-image">Image unavailable</span>';
  }

  updateCardImage(input) {
    const card = input.closest('.sfinder-card');
    const media = card?.querySelector('.dcatalog-card__media');
    if (media) media.innerHTML = this.imageMarkup(input.dataset.imageUrl || '', card.querySelector('h3')?.textContent || 'Sorella perfume');
  }

  familyLabel(value) {
    const label = String(value || '').trim();
    if (!label) return '';
    if (/floral|flower/i.test(label)) return 'Floral';
    if (/fruit/i.test(label)) return 'Fruity';
    if (/fresh|citrus|aquatic/i.test(label)) return 'Fresh';
    if (/gourmand|sweet/i.test(label)) return 'Gourmand';
    if (/herbal|green/i.test(label)) return 'Herbal';
    if (/earth|wood|oud/i.test(label)) return 'Earthy';
    if (/warm|amber|vanilla|musk/i.test(label)) return 'Warm';
    return label;
  }

  variantLabel(title, format) {
    const source = `${title || ''} ${format || ''}`;
    const size = source.match(/\b(5|8|30|50)\s*ml\b/i)?.[0]?.replace(/\s+/g, '') || '';
    if (/oil|roll/i.test(source)) return size ? `Oil · ${size}` : 'Oil';
    return size || String(title || format || '').replace(/EDP\s*\/\s*/i, '').replace(/\s*\/\s*One Size/i, '').trim();
  }

  starMarkup() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m12 1.8 3.1 6.3 7 .9-5 4.9 1.2 6.9-6.3-3.3-6.3 3.3 1.2-6.9-5-4.9 7-.9z"/></svg>'.repeat(5);
  }

  restart() {
    this.cancelCatalogRetry();
    this.index = 0;
    this.form.reset();
    this.form.hidden = false;
    this.results.hidden = true;
    this.classList.remove('is-results');
    this.querySelector('.sfinder__stage')?.classList.remove('is-results');
    this.querySelector('.sfinder__shell')?.classList.remove('is-results');
    this.querySelector('.sfinder__progress').hidden = false;
    this.renderStep();
    this.scrollIntoView({behavior:'smooth',block:'start'});
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[character]);
  }
}
if (!customElements.get('sorella-finder')) customElements.define('sorella-finder', SorellaFinder);
